/**
 * Copyright (c) HashiCorp, Inc.
 * SPDX-License-Identifier: BUSL-1.1
 */

import {
  HdsSegmentedGroup,
  HdsFormTextInputField,
  HdsTextBody,
  HdsTag,
  HdsTooltipButton,
  HdsIcon,
  HdsButton,
  HdsDropdown,
  HdsBadgeCount,
} from '@hashicorp/design-system-components/components';
import Component from '@glimmer/component';
import { on } from '@ember/modifier';
import { fn, array } from '@ember/helper';

import FilterControlGroup from './filter-bar/filter-control-group.gts';

import { type Filter } from '../../utils/filters.ts';
import './filter-bar.scss';
import { tracked } from '@glimmer/tracking';
import { eq } from '../../utils/helpers.ts';

// This lets us use generics and make sure a FieldDefinition name matches the key of a given request object
// It strips the `filter` prefix so we can definine our keys in a more ergonomic and less redundant way
// such as `createdByIds` instead of `filterCreatedByIds`. For time filters we also ignore `Absolute` so we
// can use `createdAt` since the generated client actually has several possible keys for createdAt and we have
// logic at the request level to account for this.
type StripFilterPrefix<S extends string> = S extends `filter${infer Rest}`
  ? Rest extends `${infer First}${infer Tail}`
    ? Tail extends `${infer Middle}Absolute`
      ? `${Lowercase<First>}${Middle}`
      : `${Lowercase<First>}${Tail}`
    : Rest
  : S;

export type FilterFieldName<T> = {
  [K in keyof T as StripFilterPrefix<K & string>]: K;
};

export interface FilterFieldControl<T = Record<string, unknown>> {
  name: Extract<keyof FilterFieldName<T>, string>;
  label?: string;
  type: 'text' | 'single-select' | 'multi-select' | 'lookback' | 'search';
  options?: { name: string; value: string }[];
}
export interface FilterFieldDefinition<T = Record<string, unknown>> {
  label: string;
  level?: 'primary' | 'secondary';
  controls: FilterFieldControl<T>[];
}

export interface FilterBarSignature {
  Args: {
    onFiltersApplied: (filters: Filter[]) => void;
    appliedFilters?: Filter[];
    filterFieldDefinitions: FilterFieldDefinition[];
  };

  Blocks: {
    default: [];
  };
  Element: HTMLElement;
}

export default class FilterBar extends Component<FilterBarSignature> {
  @tracked activeSecondaryFilter: FilterFieldDefinition | null = null;

  setActiveSecondaryFilter = (filter: FilterFieldDefinition | null) => {
    this.activeSecondaryFilter = filter;
  };

  handleUpdateFilters = (filters: Record<string, Filter>) => {
    this.args.onFiltersApplied(Object.values(filters));
  };

  handleClearFilters = () => {
    this.handleUpdateFilters({});
  };

  handleDismissFilter = (key: string) => {
    const updated = { ...this.appliedFilters };
    delete updated[key];
    this.handleUpdateFilters(updated);
  };

  handleTextInputChange = (name: string, event: Event) => {
    const { value } = event.target as HTMLInputElement;
    this.handleUpdateFilters({
      ...this.appliedFilters,
      [name]: {
        operator: '=',
        value: {
          type: 'string',
          value,
        },
        field: name,
      },
    });
  };

  getValue = (name: string) => {
    return (this.appliedFilters?.[name]?.value?.value as string) ?? '';
  };

  getCount = (name: string) => {
    const appliedFilter = this.appliedFilters[name];
    if (appliedFilter?.value?.value) {
      if (Array.isArray(appliedFilter.value.value)) {
        return appliedFilter.value.value.length;
      }
      return 1;
    }
    return 0;
  };

  hasCount = (name: string) => {
    return this.getCount(name) > 0;
  };

  getDefinitionCount = (filter: FilterFieldDefinition) => {
    return filter.controls.reduce((count, control) => {
      if (this.appliedFilters[control.name]) {
        return count + this.getCount(control.name);
      }
      return count;
    }, 0);
  };

  hasDefinitionCount = (filter: FilterFieldDefinition) => {
    return this.getDefinitionCount(filter) > 0;
  };

  friendlyAppliedString = (appliedFilter: Filter) => {
    const { operator, value: valueDefinition } = appliedFilter;
    const field = this.args.filterFieldDefinitions.find((f) =>
      f.controls.find((c) => c.name === appliedFilter.field),
    );

    const label = field?.label || appliedFilter.field;
    const { value } = valueDefinition;
    const { type = 'string' } = valueDefinition;
    let formattedValue = value as string;
    if (type === 'duration' && !Array.isArray(value)) {
      formattedValue = value as string;
    }
    if (type === 'list' && Array.isArray(value)) {
      formattedValue = value.join(', ');
    }

    return `${label} ${operator} ${formattedValue}`;
  };

  get appliedFilters() {
    const applied = (this.args.appliedFilters || []).reduce(
      (acc, filter) => {
        const { field } = filter;
        acc[field] = filter;
        return acc;
      },
      {} as Record<string, Filter>,
    );

    return applied;
  }

  get appliedFilterTags() {
    return Object.entries(this.appliedFilters).map(([key, filter]) => ({
      key,
      text: this.friendlyAppliedString(filter),
    }));
  }

  get appliedFiltersCount() {
    return Object.keys(this.appliedFilters).length;
  }

  get hasAppliedFilters() {
    return this.appliedFiltersCount > 0;
  }

  get primaryFilterDefinitions() {
    return this.args.filterFieldDefinitions.filter(
      (filter) => filter.level === 'primary',
    );
  }

  get secondaryFilterDefinitions() {
    return this.args.filterFieldDefinitions.filter(
      (filter) => filter.level !== 'primary',
    );
  }

  get appliedSecondaryFiltersCount() {
    return this.secondaryFilterDefinitions.reduce((count, filter) => {
      const applied = filter.controls.some(
        (control) => this.appliedFilters[control.name],
      );

      if (applied) {
        return count + 1;
      }
      return count;
    }, 0);
  }

  <template>
    <div class="filter-bar__container">
      <HdsSegmentedGroup class="filter-bar__filters" as |SG|>
        {{#each this.primaryFilterDefinitions as |filterDefinition|}}
          {{#each filterDefinition.controls as |control|}}
            {{#if (eq control.type "search")}}
              <HdsFormTextInputField
                data-test-vault-reporting-filter-bar-search={{control.name}}
                @type="search"
                name={{control.name}}
                placeholder="Search"
                @value={{this.getValue control.name}}
                @width="300px"
                {{on "change" (fn this.handleTextInputChange control.name)}}
              />
            {{else}}
              <SG.Dropdown
                data-test-vault-reporting-filter-bar-primary-dropdown={{control.name}}
                @listPosition="bottom-left"
                as |D|
              >
                <D.ToggleButton
                  @color="secondary"
                  @text={{filterDefinition.label}}
                />
                <D.Generic>
                  <FilterControlGroup
                    @controls={{array control}}
                    @appliedFilters={{this.appliedFilters}}
                    @onUpdateFilters={{this.handleUpdateFilters}}
                  />
                </D.Generic>
              </SG.Dropdown>
            {{/if}}
          {{/each}}
        {{/each}}
      </HdsSegmentedGroup>
      <HdsDropdown
        @listPosition="bottom-left"
        data-test-vault-reporting-filter-bar-secondary-dropdown
        as |D|
      >
        <D.ToggleButton
          data-test-vault-reporting-filter-bar-secondary-toggle
          @text="Filters"
          @icon="filter"
          @color="secondary"
          @count={{this.appliedSecondaryFiltersCount}}
          class="filter-bar__secondary-toggle"
        />
        <D.Generic class="filter-bar__secondary-filters">
          <ul class="filter-bar__secondary-filter-list">
            {{#each this.secondaryFilterDefinitions as |filter|}}
              <D.Interactive
                class={{if
                  (eq filter.label this.activeSecondaryFilter.label)
                  "mock-active"
                  ""
                }}
                @trailingIcon="chevron-right"
                data-test-vault-reporting-filter-bar-secondary-option={{filter.label}}
                {{on "click" (fn this.setActiveSecondaryFilter filter)}}
              >
                {{filter.label}}
                {{#if (this.hasDefinitionCount filter)}}
                  <HdsBadgeCount
                    @size="small"
                    @type="outlined"
                    @text={{this.getDefinitionCount filter}}
                  />
                {{/if}}
              </D.Interactive>
            {{/each}}
          </ul>
          <div class="filter-bar__secondary-filter-controls">
            {{#if this.activeSecondaryFilter}}
              <FilterControlGroup
                @controls={{this.activeSecondaryFilter.controls}}
                @appliedFilters={{this.appliedFilters}}
                @onUpdateFilters={{this.handleUpdateFilters}}
              />
            {{/if}}
          </div>
        </D.Generic>
      </HdsDropdown>
    </div>
    <div class="filter-bar__applied-filters">
      {{#if this.hasAppliedFilters}}
        <HdsTextBody @color="strong" @size="200" @weight="semibold">Applied
          filters ({{this.appliedFiltersCount}})</HdsTextBody>
        {{#each this.appliedFilterTags as |appliedFilter|}}
          <HdsTag
            data-test-vault-reporting-filter-bar-applied-tag={{appliedFilter.key}}
            @text={{appliedFilter.text}}
            @tooltipPlacement="right"
            @onDismiss={{fn this.handleDismissFilter appliedFilter.key}}
          />
        {{/each}}
        <HdsButton
          @size="medium"
          class="filter-bar__applied-filters__clear-button"
          @text="Clear all"
          @isInline={{true}}
          @color="tertiary"
          @icon="x"
          {{on "click" this.handleClearFilters}}
        />
      {{else}}
        <HdsTextBody
          @color="strong"
          @size="200"
          class="filter-bar__applied-filters-zero-state"
        >No filters applied
          <HdsTooltipButton
            class="filter-bar__applied-filters-zero-state__tooltip"
            @text="Use the filter bar to refine your results."
            aria-label="More information"
          >
            <HdsIcon @name="info" />
          </HdsTooltipButton></HdsTextBody>
      {{/if}}

    </div>
  </template>
}
